import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { api, ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import type {
  CommandCenterData,
  CoverageData,
  CoverageRow,
  DoraSnapshot,
  LegacyLinksData,
  TriageTemplates,
} from "../api/types";

interface DataState {
  loading: boolean;
  commandCenter: CommandCenterData | null;
  coverage: CoverageData | null;
  coverageByName: Record<string, CoverageRow>;
  dora: DoraSnapshot | null;
  triage: TriageTemplates | null;
  links: LegacyLinksData | null;
  error: string | null;
}

const Ctx = createContext<DataState | null>(null);

const initial: DataState = {
  loading: true,
  commandCenter: null,
  coverage: null,
  coverageByName: {},
  dora: null,
  triage: null,
  links: null,
  error: null,
};

// Registry artifacts are now served by command-center-api behind Keycloak auth
// (GET /command-center/registry/<slug>), NOT fetched anonymously as same-origin
// static files. This keeps catalog topology (service names, Slack channels, runbook
// paths, integration partners) behind SSO and removes the cross-repo S3-sync coupling
// that previously left the SPA showing "Could not load …" when the JSONs weren't synced.
// Each call returns null on failure (api.get throws → caught) so one missing artifact
// doesn't blank the whole app.
async function loadRegistry<T>(slug: string): Promise<T | null> {
  try {
    return await api.get<T>(`/command-center/registry/${slug}`);
  } catch (err) {
    // 404 `unavailable` (artifact not baked) or 401 (auth) → render empty state.
    if (!(err instanceof ApiError)) console.error(`registry/${slug} failed`, err);
    return null;
  }
}

export function DataProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DataState>(initial);
  const { state: auth } = useAuth();

  useEffect(() => {
    // The registry endpoints are Keycloak-gated. Only fetch once auth is established and
    // the access-token getter is wired — otherwise the request goes out with no Bearer
    // header and the API returns 401 ("Not authenticated"). Re-runs when auth flips to
    // "authenticated" (deps below), so the data loads right after login completes.
    if (auth.status !== "authenticated") return;

    let cancelled = false;
    (async () => {
      const [commandCenter, coverage, dora, triage, links] = await Promise.all([
        loadRegistry<CommandCenterData>("command-center-data"),
        loadRegistry<CoverageData>("coverage"),
        loadRegistry<DoraSnapshot>("dora"),
        loadRegistry<TriageTemplates>("triage-templates"),
        loadRegistry<LegacyLinksData>("links"),
      ]);
      if (cancelled) return;
      const coverageByName: Record<string, CoverageRow> = {};
      coverage?.rows.forEach((r) => { coverageByName[r.name] = r; });
      setState({
        loading: false,
        commandCenter,
        coverage,
        coverageByName,
        dora,
        triage,
        links,
        error: commandCenter ? null : "Could not load command-center-data (registry unavailable or not authenticated)",
      });
    })();
    return () => { cancelled = true; };
  }, [auth.status]);

  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

export function useData() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useData() must be used inside <DataProvider>");
  return ctx;
}
